import { FocusEvent, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { PopperProps } from '@mui/material';

import { STORAGE_KEY_APP_INSPECTOR_NUDGE_DISMISSED, STORAGE_KEY_APP_INSPECTOR_VISITED } from '@localstack/constants';
import { useLocalStorage } from '@localstack/services';

const HOVER_OPEN_DELAY_MS = 350;
const HOVER_CLOSE_DELAY_MS = 150;

const HEADER_SELECTOR = '.base-header-container';
const HEADER_POPOVER_GAP = 8;
const DEFAULT_HEADER_GAP = 12;

// Instance-page navigation remounts the component, so these values are latched per page load.
const nudgeSession = {
  hasAutoShown: false,
  hasTrackedNudgeShown: false,
};

type UseNudgeVisibilityProps = {
  anchorEl: HTMLSpanElement | null;
  isHovered: boolean;
  onAutoShow: () => void;
};

type UseNudgeVisibilityReturn = {
  autoShowIntro: boolean;
  dismissIntro: () => void;
  hasVisited: boolean;
  isIntroOpen: boolean;
  markVisited: () => void;
  shouldAnimateAutoShow: boolean;
  showDot: boolean;
};

export const useNudgeVisibility = ({
  anchorEl,
  isHovered,
  onAutoShow,
}: UseNudgeVisibilityProps): UseNudgeVisibilityReturn => {
  const [shouldAnimateAutoShow] = useState(() => !nudgeSession.hasAutoShown);
  const {
    data: visited,
    setData: setVisited,
    isLoading: isVisitedLoading,
  } = useLocalStorage<boolean>({
    key: STORAGE_KEY_APP_INSPECTOR_VISITED,
    defaultValue: false,
  });
  const {
    data: introDismissed,
    setData: setIntroDismissed,
    isLoading: isIntroDismissedLoading,
  } = useLocalStorage<boolean>({
    key: STORAGE_KEY_APP_INSPECTOR_NUDGE_DISMISSED,
    defaultValue: false,
  });

  // Waiting for both flags prevents returning users from seeing the intro for one frame.
  const flagsLoaded = !isVisitedLoading && !isIntroDismissedLoading;
  const isUnvisited = flagsLoaded && !visited;
  const autoShowIntro = isUnvisited && !introDismissed;
  const showDot = isUnvisited;
  const showIntro = autoShowIntro || (showDot && isHovered);
  const isIntroOpen = showIntro && !!anchorEl;

  useEffect(() => {
    if (!autoShowIntro || !isIntroOpen) return;

    nudgeSession.hasAutoShown = true;
    if (!nudgeSession.hasTrackedNudgeShown) {
      nudgeSession.hasTrackedNudgeShown = true;
      onAutoShow();
    }
  }, [autoShowIntro, isIntroOpen, onAutoShow]);

  const markVisited = useCallback(() => setVisited(true), [setVisited]);
  const dismissIntro = useCallback(() => setIntroDismissed(true), [setIntroDismissed]);

  return {
    autoShowIntro,
    dismissIntro,
    hasVisited: visited === true,
    isIntroOpen,
    markVisited,
    shouldAnimateAutoShow,
    showDot,
  };
};

type UseHoverIntentReturn<T extends HTMLElement> = {
  handleBlur: () => void;
  handleFocus: (event: FocusEvent<T>) => void;
  handleHoverEnd: () => void;
  handleHoverStart: () => void;
  isHovered: boolean;
  isKeyboardFocused: boolean;
  resetHover: () => void;
};

export const useHoverIntent = <T extends HTMLElement>(): UseHoverIntentReturn<T> => {
  const [isHovered, setIsHovered] = useState(false);
  const [isKeyboardFocused, setIsKeyboardFocused] = useState(false);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const scheduleHover = useCallback((next: boolean) => {
    clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => setIsHovered(next), next ? HOVER_OPEN_DELAY_MS : HOVER_CLOSE_DELAY_MS);
  }, []);
  const handleHoverStart = useCallback(() => scheduleHover(true), [scheduleHover]);
  const handleHoverEnd = useCallback(() => scheduleHover(false), [scheduleHover]);
  const handleFocus = useCallback((event: FocusEvent<T>) => {
    setIsKeyboardFocused(!event.currentTarget.matches(':hover'));
    setIsHovered(true);
  }, []);
  const handleBlur = useCallback(() => {
    setIsKeyboardFocused(false);
    setIsHovered(false);
  }, []);
  const resetHover = useCallback(() => {
    clearTimeout(hoverTimerRef.current);
    setIsHovered(false);
  }, []);

  useEffect(() => () => clearTimeout(hoverTimerRef.current), []);

  return {
    handleBlur,
    handleFocus,
    handleHoverEnd,
    handleHoverStart,
    isHovered,
    isKeyboardFocused,
    resetHover,
  };
};

type HeaderOffsetModifiers = NonNullable<PopperProps['modifiers']>;

export const useHeaderOffsetModifiers = (anchorEl: HTMLSpanElement | null): HeaderOffsetModifiers => {
  const [headerGap, setHeaderGap] = useState(DEFAULT_HEADER_GAP);

  // Measure before paint so the card opens at the right offset without snapping into place.
  useLayoutEffect(() => {
    if (!anchorEl) return undefined;

    const measure = () => {
      const header = anchorEl.closest(HEADER_SELECTOR);
      if (!header) {
        setHeaderGap(DEFAULT_HEADER_GAP);
        return;
      }
      setHeaderGap(
        header.getBoundingClientRect().bottom - anchorEl.getBoundingClientRect().bottom + HEADER_POPOVER_GAP,
      );
    };

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [anchorEl]);

  return useMemo(() => [{ name: 'offset', options: { offset: [0, headerGap] } }], [headerGap]);
};
